import { isSameSignedSource } from './signed-source.ts';

/** Ignore re-signing while requiring the final URL for the same recording. */
export function isRecordingHandover(
  previous: string | null,
  next: string | null,
): boolean {
  if (!previous || !next) return false;
  try {
    const before = new URL(previous);
    const after = new URL(next);
    if (!before.pathname.endsWith('/live.m3u8')) return false;
    before.pathname = before.pathname.replace(/live\.m3u8$/, 'playlist.m3u8');
    return isSameSignedSource(before.href, after.href);
  } catch {
    return false;
  }
}

/**
 * Install before changing src, since load() resets the position and pauses playback.
 * The returned callback takes play and pause requests made before the new source
 * has loaded, so the latest one decides how playback is restored.
 */
export function preserveRecordingPlayback(
  media: EventTarget & {
    currentTime: number;
    duration: number;
    paused: boolean;
    play(): Promise<void>;
    pause(): void;
  },
  signal: AbortSignal,
  onError: (error: unknown) => void,
  onRestored: () => void = () => {},
): (playing: boolean) => void {
  const position = Number.isFinite(media.currentTime)
    ? Math.max(0, media.currentTime)
    : 0;
  let resume = !media.paused;
  media.addEventListener('loadedmetadata', () => {
    // play() at the ended boundary can restart from the beginning.
    const end = Math.max(0, media.duration - (resume ? 0.1 : 0));
    media.currentTime = Number.isFinite(media.duration)
      ? Math.min(position, end)
      : position;
    if (resume) {
      media.play().catch(onError);
    } else {
      media.pause();
    }
    onRestored();
  }, { once: true, signal });
  return (playing) => {
    resume = playing;
  };
}
